import { useState } from 'react';
import { Check, X, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { aiAssistantAPI } from '@shared/apiClient';
import toast from 'react-hot-toast';

export default function PendingActionCard({ action, onConfirmed, onCancelled }) {
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const { data } = await aiAssistantAPI.confirmAction(action.id);
      setStatus('confirmed');
      toast.success('Action completed');
      onConfirmed?.(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
      setStatus('failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      await aiAssistantAPI.cancelAction(action.id);
      setStatus('cancelled');
      onCancelled?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Cancel failed');
    } finally {
      setLoading(false);
    }
  };

  if (status === 'confirmed') {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800">
        <Check className="h-4 w-4 flex-shrink-0" />
        <span>Confirmed: {action.summary}</span>
      </div>
    );
  }

  if (status === 'cancelled') {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-600">
        <X className="h-4 w-4 flex-shrink-0" />
        <span>Cancelled: {action.summary}</span>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
        <X className="h-4 w-4 flex-shrink-0" />
        <span>Failed: {action.summary}</span>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
      <p className="text-sm font-medium text-amber-900">Confirm this action?</p>
      <p className="text-sm text-amber-800">{action.summary}</p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleConfirm}
          disabled={loading}
          className={clsx(
            'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md',
            'bg-green-600 text-white hover:bg-green-700 disabled:opacity-50'
          )}
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Confirm
        </button>
        <button
          type="button"
          onClick={handleCancel}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <X className="h-3.5 w-3.5" />
          Cancel
        </button>
      </div>
    </div>
  );
}
